import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import Fuse from 'fuse.js';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  LayoutDashboard,
  Package,
  Box,
  Bell,
  History,
  Truck,
  Settings,
  Plus,
  Mic,
  Download,
} from 'lucide-react';
import { useProductStore } from '@/stores/productStore';

interface CommandItem {
  id: string;
  type: 'product' | 'action' | 'page';
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  shortcut?: string;
  onSelect: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNewProduct?: () => void;
  onVoiceInput?: () => void;
  onExport?: () => void;
}

export function CommandPalette({
  open,
  onOpenChange,
  onNewProduct,
  onVoiceInput,
  onExport,
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { products } = useProductStore();

  // Reset query when closing
  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  // Handle keyboard shortcut to open
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  const handleSelect = useCallback((callback: () => void) => {
    onOpenChange(false);
    callback();
  }, [onOpenChange]);

  // Build items list
  const items = useMemo<CommandItem[]>(() => {
    const allItems: CommandItem[] = [
      // Pages
      {
        id: 'page-dashboard',
        type: 'page',
        title: 'Dashboard',
        subtitle: 'Vue d\'ensemble',
        icon: <LayoutDashboard className="w-4 h-4" />,
        shortcut: 'Ctrl+1',
        onSelect: () => handleSelect(() => navigate('/dashboard')),
      },
      {
        id: 'page-inventory',
        type: 'page',
        title: 'Inventaire',
        subtitle: 'Gestion des stocks par zone',
        icon: <Package className="w-4 h-4" />,
        shortcut: 'Ctrl+2',
        onSelect: () => handleSelect(() => navigate('/inventory')),
      },
      {
        id: 'page-products',
        type: 'page',
        title: 'Produits',
        subtitle: 'Catalogue des produits',
        icon: <Box className="w-4 h-4" />,
        shortcut: 'Ctrl+3',
        onSelect: () => handleSelect(() => navigate('/products')),
      },
      {
        id: 'page-alerts',
        type: 'page',
        title: 'Alertes',
        subtitle: 'Stocks bas et DLC',
        icon: <Bell className="w-4 h-4" />,
        shortcut: 'Ctrl+4',
        onSelect: () => handleSelect(() => navigate('/alerts')),
      },
      {
        id: 'page-history',
        type: 'page',
        title: 'Historique',
        subtitle: 'Mouvements de stock',
        icon: <History className="w-4 h-4" />,
        shortcut: 'Ctrl+5',
        onSelect: () => handleSelect(() => navigate('/history')),
      },
      {
        id: 'page-suppliers',
        type: 'page',
        title: 'Fournisseurs',
        subtitle: 'Gestion des fournisseurs',
        icon: <Truck className="w-4 h-4" />,
        onSelect: () => handleSelect(() => navigate('/suppliers')),
      },
      {
        id: 'page-settings',
        type: 'page',
        title: 'Paramètres',
        subtitle: 'Configuration de l\'application',
        icon: <Settings className="w-4 h-4" />,
        onSelect: () => handleSelect(() => navigate('/settings')),
      },

      // Actions
      {
        id: 'action-new-product',
        type: 'action',
        title: 'Nouveau produit',
        subtitle: 'Ajouter un produit au catalogue',
        icon: <Plus className="w-4 h-4" />,
        shortcut: 'Ctrl+N',
        onSelect: () => handleSelect(() => onNewProduct?.()),
      },
      {
        id: 'action-voice',
        type: 'action',
        title: 'Saisie vocale',
        subtitle: 'Ajouter des mouvements par la voix',
        icon: <Mic className="w-4 h-4" />,
        shortcut: 'Ctrl+Shift+V',
        onSelect: () => handleSelect(() => onVoiceInput?.()),
      },
      {
        id: 'action-export',
        type: 'action',
        title: 'Exporter l\'inventaire',
        subtitle: 'Export PDF ou CSV',
        icon: <Download className="w-4 h-4" />,
        shortcut: 'Ctrl+E',
        onSelect: () => handleSelect(() => onExport?.()),
      },

      // Products (dynamic)
      ...products.slice(0, 50).map((p) => ({
        id: `product-${p.id}`,
        type: 'product' as const,
        title: p.nom,
        subtitle: `Stock: ${p.total_stock ?? 0} ${p.unite || 'unités'} • ${p.categorie || 'Sans catégorie'}`,
        icon: <Box className="w-4 h-4 text-primary" />,
        onSelect: () => handleSelect(() => navigate(`/products?search=${encodeURIComponent(p.nom)}`)),
      })),
    ];

    return allItems;
  }, [products, navigate, handleSelect, onNewProduct, onVoiceInput, onExport]);

  // Fuzzy search
  const fuse = useMemo(
    () =>
      new Fuse(items, {
        keys: ['title', 'subtitle'],
        threshold: 0.3,
        includeScore: true,
      }),
    [items]
  );

  const filteredItems = useMemo(() => {
    if (!query.trim()) {
      // Show pages and actions first when no query
      return [
        ...items.filter((i) => i.type === 'page'),
        ...items.filter((i) => i.type === 'action'),
        ...items.filter((i) => i.type === 'product').slice(0, 5),
      ];
    }

    return fuse.search(query).map((r) => r.item);
  }, [query, items, fuse]);

  const groupedItems = useMemo(() => {
    const groups = {
      page: [] as CommandItem[],
      action: [] as CommandItem[],
      product: [] as CommandItem[],
    };

    filteredItems.forEach((item) => {
      if (item.type in groups) {
        groups[item.type].push(item);
      }
    });

    return groups;
  }, [filteredItems]);

  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => onOpenChange(false)}
          />

          {/* Command Palette */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.15 }}
            className="fixed left-1/2 top-[20%] -translate-x-1/2 w-full max-w-xl z-50"
          >
            <Command
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
              loop
            >
              {/* Input */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <Search className="w-5 h-5 text-gray-400" />
                <Command.Input
                  value={query}
                  onValueChange={setQuery}
                  placeholder="Rechercher produits, pages, actions..."
                  className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-400"
                  autoFocus
                />
                <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400 rounded">
                  Esc
                </kbd>
              </div>

              {/* Results */}
              <Command.List className="max-h-80 overflow-y-auto p-2">
                <Command.Empty className="py-6 text-center text-gray-500">
                  Aucun résultat pour "{query}"
                </Command.Empty>

                {/* Pages */}
                {groupedItems.page.length > 0 && (
                  <Command.Group
                    heading={
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-2">
                        Pages
                      </span>
                    }
                  >
                    {groupedItems.page.map((item) => (
                      <CommandItem key={item.id} item={item} isMac={isMac} />
                    ))}
                  </Command.Group>
                )}

                {/* Actions */}
                {groupedItems.action.length > 0 && (
                  <Command.Group
                    heading={
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-2">
                        Actions rapides
                      </span>
                    }
                  >
                    {groupedItems.action.map((item) => (
                      <CommandItem key={item.id} item={item} isMac={isMac} />
                    ))}
                  </Command.Group>
                )}

                {/* Products */}
                {groupedItems.product.length > 0 && (
                  <Command.Group
                    heading={
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-2">
                        Produits
                      </span>
                    }
                  >
                    {groupedItems.product.slice(0, 10).map((item) => (
                      <CommandItem key={item.id} item={item} isMac={isMac} />
                    ))}
                  </Command.Group>
                )}
              </Command.List>

              {/* Footer */}
              <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">↑↓</kbd>
                    naviguer
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">↵</kbd>
                    sélectionner
                  </span>
                </div>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                    {isMac ? '⌘' : 'Ctrl'}+K
                  </kbd>
                  ouvrir/fermer
                </span>
              </div>
            </Command>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Individual command item component
function CommandItem({ item, isMac }: { item: CommandItem; isMac: boolean }) {
  return (
    <Command.Item
      value={item.title}
      onSelect={item.onSelect}
      className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-gray-700 dark:text-gray-200 data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary transition-colors"
    >
      <span className="flex-shrink-0 text-gray-400">{item.icon}</span>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{item.title}</div>
        {item.subtitle && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {item.subtitle}
          </div>
        )}
      </div>
      {item.shortcut && (
        <kbd className="flex-shrink-0 hidden sm:inline-flex items-center gap-0.5 px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400 rounded">
          {item.shortcut.replace('Ctrl', isMac ? '⌘' : 'Ctrl')}
        </kbd>
      )}
    </Command.Item>
  );
}

export default CommandPalette;
